import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, shareReplay, take, withLatestFrom } from 'rxjs/operators';

// Redux
import { navigate, setTitle, dispatchInfoMessage, openWindow } from 'src/app/actions/core.actions';
import { setCommonIdBrowserTitle } from 'src/app/actions/common-id/common-id-core.actions';
import * as CommonIdFindUniversitiesActions from 'src/app/actions/common-id/common-id-find-universities.actions';
import * as CommonIdGetDesiredUniversitiesActions from 'src/app/actions/common-id/common-id-get-desired-universities.actions';
import { RootState } from 'src/app/reducers';
import { getCommonIdSignedInUser } from 'src/app/selectors/common-id/common-id-auth.selectors';
import { getCommonIdFindUniversitiesResult } from 'src/app/selectors/common-id/common-id-find-universities.selectors';
import { getCommonIdGetDesiredUniversitiesResult } from 'src/app/selectors/common-id/common-id-get-desired-universities.selectors';
import * as StaticDataSelectors from '../../../../selectors/static-data.selectors';

// models
import { CommonIdUser } from 'src/app/models/common-id/common-id-user';
import { StaticCommonData } from '../../../../models/static-common-data';
import { CommonIdFindUniversitiesRequest } from 'src/app/models/common-id/common-id-find-universities-request';
import { CommonIdFindUniversitiesResponse } from 'src/app/models/common-id/common-id-find-universities-response';
import { CommonIdGetDesiredUniversitiesRequest } from 'src/app/models/common-id/common-id-get-desired-universities-request';

// config
import { enter } from 'src/app/resources/animations';
import { RoutingPathResolver } from 'src/app/app-routing-path-resolver';
import { COMMON_ID_PREFECTURES, COMMON_TEST_UNIVERSITY_ID } from 'src/app/resources/common-id-config';
import { environment } from 'src/environments/environment';

import { Log } from 'src/app/utils/log';

interface UniversityInfo {
  id: string;
  name: string;
  prefName: string;
  univType: string;
  univTypeName: string;
  hasKakomon: boolean;
}

@Component({
  selector: 'app-common-id-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  animations: [enter]
})
export class CommonIdHomeComponent implements OnInit, OnDestroy, AfterViewInit {
  private LOG_SOURCE = this.constructor.name;
  private title = 'ホーム';
  private userSubscription: Subscription;
  private findUniversitiesSubscription: Subscription;
  private syncSubscription: Subscription;

  signedInUser$: Observable<CommonIdUser>;
  staticCommonData$: Observable<StaticCommonData>;
  isSyncing = false;
  isRegistered = false;
  isLoading = true;

  universities: UniversityInfo[] = [];

  constructor(private store: Store<RootState>) {}

  ngOnInit() {
    this.store.dispatch(setCommonIdBrowserTitle({ subTitle: this.title }));
    setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));

    this.setUpUser();
    this.setUpStaticCommonData();
    this.setUpRegisteredUniversities(); // 志望校の登録があるかを判定し、志望校情報を取得する
  }

  ngOnDestroy() {
    this.store.dispatch(CommonIdFindUniversitiesActions.initializeCommonIdFindUniversitiesResult());
    this.store.dispatch(CommonIdGetDesiredUniversitiesActions.initializeCommonIdGetDesiredUniversitiesResult());
    if (this.userSubscription) this.userSubscription.unsubscribe();
    if (this.findUniversitiesSubscription) this.findUniversitiesSubscription.unsubscribe();
    if (this.syncSubscription) this.syncSubscription.unsubscribe();
  }

  ngAfterViewInit() {
    setTimeout(() => window.scrollTo(0, 0));
  }

  onViewKakomonClick() {
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchUnivDetail(COMMON_TEST_UNIVERSITY_ID) }));
  }

  onMenuClick(menu: string) {
    switch (menu) {
      case 'search-univ':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchUniv() }));
        break;
      case 'search-by-categories':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchByCategories() }));
        break;
      case 'playlists':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdPlaylists() }));
        break;
      case 'mylist':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdMylistUniversities() }));
        break;
    }
  }

  onKakomonClick(univId: string) {
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchUnivDetail(univId) }));
  }

  goToManabiProfile() {
    this.store.dispatch(openWindow({ url: environment.commonIdConfig.profileUrl }));
  }

  onSyncShibokoClick() {
    if (this.isSyncing) return;
    this.isLoading = true;
    this.isSyncing = true;

    this.store.dispatch(CommonIdGetDesiredUniversitiesActions.initializeCommonIdGetDesiredUniversitiesResult());

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return;

    const request: CommonIdGetDesiredUniversitiesRequest = { refreshToken };
    this.store.dispatch(CommonIdGetDesiredUniversitiesActions.commonIdGetDesiredUniversities({ request }));

    this.syncSubscription = this.store
      .select(getCommonIdGetDesiredUniversitiesResult)
      .pipe(
        filter(result => result != null),
        take(1),
        withLatestFrom(this.staticCommonData$, this.store.select(getCommonIdFindUniversitiesResult)) // 志望校の最新情報が更新されるまで待つ
      )
      .subscribe(([desiredUniversitiesResult, staticCommonData, findUniversitiesResult]) => {
        if (desiredUniversitiesResult.success) {
          if (findUniversitiesResult.response.length === 0) {
            // 志望校の登録がなくなった場合は、志望校の存在フラグをoffに設定し、大学情報も空にする
            this.isRegistered = false;
            this.universities = [];
          } else {
            this.isRegistered = true;
            this.universities = this.mapToUniversityInfoList(findUniversitiesResult.response, staticCommonData);
          }
        } else {
          this.handleUniversityLoadError('志望校の最新情報の取得(CommonIdGetDesiredUniversities)', desiredUniversitiesResult.error);
        }
        this.isLoading = false;
        this.isSyncing = false;
      });
  }

  getPassnaviUrl(univId: string, page: string): string {
    const univIdWithoutD = univId.replace('D', '');
    return `https://passnavi.obunsha.co.jp/univ/${univIdWithoutD}/${page}/?utm_source=nd-top`;
  }

  private getRefreshToken(): string | null {
    const tokenInfoString = localStorage.getItem('tokenInfo');
    if (!tokenInfoString) {
      this.isSyncing = false;
      this.isLoading = false;
      this.store.dispatch(dispatchInfoMessage({ message: '志望校の最新情報の取得に失敗しました。トークン情報がありません。' }));
      return null;
    }
    const tokenInfo = JSON.parse(tokenInfoString);
    return tokenInfo.refreshToken;
  }

  private mapToUniversityInfoList(response: CommonIdFindUniversitiesResponse[], staticCommonData: StaticCommonData): UniversityInfo[] {
    return response.map(univ => {
      return {
        id: univ.universityId,
        name: univ.universityName,
        prefName: COMMON_ID_PREFECTURES.find(pref => pref.id === univ.prefectureId)?.name,
        univType: univ.universityTypeId,
        univTypeName: this.getUnivTypeName(staticCommonData, univ.universityTypeId),
        hasKakomon: univ.isSearchable && univ.hasExternalData
      };
    });
  }

  private handleUniversityLoadError(source: string, error: any) {
    Log.warn(this.LOG_SOURCE, `${source} error: err.code: ${error ? error.code : 'none'}`, error);
    this.store.dispatch(
      dispatchInfoMessage({
        message: error ? `[${error.code}] ${error.message}` : 'エラーが発生しました'
      })
    );
  }

  private setUpUser() {
    this.signedInUser$ = this.store.select(getCommonIdSignedInUser).pipe(filter<CommonIdUser>(it => it != null));
  }

  private setUpStaticCommonData() {
    this.staticCommonData$ = this.store.select(StaticDataSelectors.getStaticCommonData).pipe(
      filter(it => it != null),
      shareReplay(1)
    );
  }

  private setUpRegisteredUniversities() {
    this.userSubscription = this.signedInUser$.pipe(take(1)).subscribe(user => {
      this.isRegistered = user.desiredUniversities && user.desiredUniversities.length > 0;

      if (this.isRegistered) {
        this.store.dispatch(CommonIdFindUniversitiesActions.initializeCommonIdFindUniversitiesResult());

        const universityIds = [...new Set(user.desiredUniversities.map(univ => univ.split('-')[0]))];
        const request: CommonIdFindUniversitiesRequest = { universityIds };
        this.store.dispatch(CommonIdFindUniversitiesActions.commonIdFindUniversities({ request }));

        this.findUniversitiesSubscription = combineLatest([
          this.staticCommonData$,
          this.store.select(getCommonIdFindUniversitiesResult).pipe(filter(it => it != null))
        ])
          .pipe(take(1))
          .subscribe(([staticCommonData, result]) => {
            if (result.success) {
              this.universities = this.mapToUniversityInfoList(result.response, staticCommonData);
              this.isLoading = false;
            } else {
              this.handleUniversityLoadError('志望校の大学情報の取得(CommonIdFindUniversities)', result.error);
            }
          });
      }
    });
  }

  private getUnivTypeName(staticCommonData: StaticCommonData, univTypeId: string): string {
    const univType = staticCommonData.universityTypes.find(type => type.id === univTypeId);
    return univType ? univType.name : '';
  }
}
